import React, { useState, useEffect, useRef } from 'react';

const PronunciationChecker = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [status, setStatus] = useState({ message: 'Loading Speech SDK...', type: 'processing' });
  const [result, setResult] = useState(null);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Azure Speech Service Configuration
   const AZURE_SPEECH_KEY = "jnjnk";
  const AZURE_REGION = "eastus2";

  const speechRecognizerRef = useRef(null);
  const sdkRef = useRef(null);

  // Load Azure Speech SDK
  useEffect(() => {
    const loadSpeechSDK = async () => {
      try {
        if (window.SpeechSDK && window.SpeechSDK.SpeechConfig) {
          sdkRef.current = window.SpeechSDK;
          setSdkLoaded(true);
          setStatus({ message: 'Ready! Start recording to analyze your speech', type: 'success' });
          return;
        }

        setStatus({ message: 'Loading Speech SDK...', type: 'processing' });

        const script = document.createElement('script');
        script.src = 'https://aka.ms/csspeech/jsbrowserpackageraw';
        
        script.onload = () => {
          const checkSDK = () => {
            if (window.SpeechSDK && 
                window.SpeechSDK.SpeechConfig && 
                window.SpeechSDK.SpeechRecognizer &&
                window.SpeechSDK.PronunciationAssessmentConfig) {
              
              sdkRef.current = window.SpeechSDK;
              setSdkLoaded(true);
              setStatus({ message: 'Ready! Start recording to analyze your speech', type: 'success' });
            } else {
              setTimeout(checkSDK, 100);
            }
          };
          
          setTimeout(checkSDK, 100);
        };
        
        script.onerror = () => {
          setStatus({ message: 'Failed to load Speech SDK', type: 'error' });
        };
        
        document.head.appendChild(script);
      } catch (error) {
        setStatus({ message: 'Error loading Speech SDK', type: 'error' });
      }
    };

    loadSpeechSDK();
  }, []);

  const validateConfiguration = () => {
    if (!sdkLoaded || !sdkRef.current) {
      setStatus({ message: 'Speech SDK is still loading', type: 'error' });
      return false;
    }
    
    if (!AZURE_SPEECH_KEY || AZURE_SPEECH_KEY === "your_azure_speech_key_here") {
      setStatus({ message: 'Please configure Azure Speech subscription key', type: 'error' });
      return false;
    }
    
    if (!AZURE_REGION || AZURE_REGION === "your_region") {
      setStatus({ message: 'Please configure Azure Speech service region', type: 'error' });
      return false;
    }
    
    return true;
  };

  const startAssessment = async () => {
    if (!validateConfiguration()) return;

    try {
      setStatus({ message: 'Starting recording...', type: 'processing' });
      
      if (speechRecognizerRef.current) {
        try {
          speechRecognizerRef.current.close();
        } catch (e) {
          console.warn('Error closing previous recognizer:', e);
        }
        speechRecognizerRef.current = null;
      }

      const SDK = sdkRef.current;
      
      const speechConfig = SDK.SpeechConfig.fromSubscription(AZURE_SPEECH_KEY, AZURE_REGION);
      speechConfig.speechRecognitionLanguage = 'en-US';
      
      const audioConfig = SDK.AudioConfig.fromDefaultMicrophoneInput();
      
      const pronunciationAssessmentConfig = new SDK.PronunciationAssessmentConfig(
        "",
        SDK.PronunciationAssessmentGradingSystem.HundredMark,
        SDK.PronunciationAssessmentGranularity.Phoneme,
        true
      );
      
      try {
        if (typeof pronunciationAssessmentConfig.enableProsodyAssessment === 'function') {
          pronunciationAssessmentConfig.enableProsodyAssessment();
        }
      } catch (prosodyError) {
        console.warn('Prosody assessment not available:', prosodyError);
      }
      
      speechRecognizerRef.current = new SDK.SpeechRecognizer(speechConfig, audioConfig);
      pronunciationAssessmentConfig.applyTo(speechRecognizerRef.current);
      
      speechRecognizerRef.current.sessionStarted = (s, e) => {
        setStatus({ message: 'Recording... Speak clearly!', type: 'recording' });
        setIsRecording(true);
      };
      
      speechRecognizerRef.current.sessionStopped = (s, e) => {
        setIsRecording(false);
      };
      
      speechRecognizerRef.current.recognizing = (s, e) => {
        setStatus({ message: `Listening: "${e.result.text}"`, type: 'recording' });
      };
      
      speechRecognizerRef.current.recognized = (s, e) => {
        if (e.result.reason === SDK.ResultReason.RecognizedSpeech) {
          setStatus({ message: 'Analyzing speech...', type: 'processing' });
          processResults(e.result);
        } else if (e.result.reason === SDK.ResultReason.NoMatch) {
          setStatus({ message: 'No speech detected', type: 'error' });
          setIsRecording(false);
        }
      };
      
      speechRecognizerRef.current.canceled = (s, e) => {
        if (e.reason === SDK.CancellationReason.Error) {
          setStatus({ message: `Error: ${e.errorDetails}`, type: 'error' });
        } else {
          setStatus({ message: 'Recognition canceled', type: 'error' });
        }
        setIsRecording(false);
      };
      
      speechRecognizerRef.current.startContinuousRecognitionAsync(
        () => {
          console.log('Recognition started');
        },
        (error) => {
          setStatus({ message: `Error: ${error}`, type: 'error' });
          setIsRecording(false);
        }
      );
      
    } catch (error) {
      setStatus({ message: `Error: ${error.message}`, type: 'error' });
      setIsRecording(false);
    }
  };

  const stopAssessment = () => {
    if (speechRecognizerRef.current && isRecording) {
      speechRecognizerRef.current.stopContinuousRecognitionAsync(
        () => {
          setStatus({ message: 'Processing results...', type: 'processing' });
        },
        (error) => {
          setStatus({ message: `Error: ${error}`, type: 'error' });
        }
      );
    }
  };

  const processResults = (result) => {
    try {
      const SDK = sdkRef.current;
      const pronunciationResult = SDK.PronunciationAssessmentResult.fromResult(result);
      const jsonResult = result.properties.getProperty(SDK.PropertyId.SpeechServiceResponse_JsonResult);
      const detailedResult = JSON.parse(jsonResult);
      
      // Extract metrics
      const accuracy = pronunciationResult.accuracyScore || 0;
      const fluency = pronunciationResult.fluencyScore || 0;
      const prosody = pronunciationResult.prosodyScore || 0;
      const pronunciation = pronunciationResult.pronunciationScore || accuracy;
      
      // Calculate derived metrics
      const overallScore = prosody > 0 
        ? (accuracy * 0.3 + fluency * 0.25 + prosody * 0.25 + pronunciation * 0.2)
        : (accuracy * 0.4 + fluency * 0.35 + pronunciation * 0.25);
      
      const vocabulary = calculateVocabularyScore(detailedResult);
      const grammar = calculateGrammarScore(result.text);
      
      // Process word-level and phoneme-level data
      const wordAnalysis = extractWordAnalysis(detailedResult);
      const phonemeAnalysis = extractPhonemeAnalysis(detailedResult);
      
      const processedResult = {
        // Basic info
        spokenText: result.text,
        isCorrect: overallScore >= 70,
        
        // Main scores
        overallScore: Math.round(overallScore),
        accuracy: Math.round(accuracy),
        fluency: Math.round(fluency),
        prosody: Math.round(prosody),
        pronunciation: Math.round(pronunciation),
        vocabulary: Math.round(vocabulary),
        grammar: Math.round(grammar),
        
        // Detailed analysis
        wordAnalysis,
        phonemeAnalysis,
        
        // Raw data
        rawJson: detailedResult,
        
        // Metadata
        hasProsody: prosody > 0,
        wordCount: result.text.split(' ').length,
        duration: detailedResult.NBest?.[0]?.Words?.length || 0,
        
        // Feedback
        feedback: generateFeedback(overallScore, accuracy, fluency, prosody, pronunciation)
      };
      
      setResult(processedResult);
      setStatus({ 
        message: processedResult.isCorrect ? 'Analysis complete! 🎉' : 'Analysis complete - room for improvement 📈', 
        type: processedResult.isCorrect ? 'success' : 'warning' 
      });
      
    } catch (error) {
      console.error('Error processing results:', error);
      setStatus({ message: `Processing error: ${error.message}`, type: 'error' });
    }
  };

  const extractWordAnalysis = (detailedResult) => {
    try {
      const words = detailedResult.NBest?.[0]?.Words || [];
      return words.map(word => ({
        word: word.Word,
        accuracyScore: word.PronunciationAssessment?.AccuracyScore || 0,
        errorType: word.PronunciationAssessment?.ErrorType || 'None',
        confidence: word.Confidence || 0,
        duration: word.Duration || 0,
        phonemes: word.Phonemes || []
      }));
    } catch (error) {
      return [];
    }
  };

  const extractPhonemeAnalysis = (detailedResult) => {
    try {
      const phonemes = [];
      const words = detailedResult.NBest?.[0]?.Words || [];
      
      words.forEach(word => {
        if (word.Phonemes) {
          word.Phonemes.forEach(phoneme => {
            phonemes.push({
              phoneme: phoneme.Phoneme,
              accuracyScore: phoneme.PronunciationAssessment?.AccuracyScore || 0,
              word: word.Word,
              duration: phoneme.Duration || 0
            });
          });
        }
      });
      
      return phonemes;
    } catch (error) {
      return [];
    }
  };

  const calculateVocabularyScore = (detailedResult) => {
    try {
      const words = detailedResult.NBest?.[0]?.Words || [];
      const uniqueWords = new Set(words.map(w => w.Word.toLowerCase()));
      const avgWordLength = words.reduce((sum, w) => sum + w.Word.length, 0) / words.length;
      
      // Simple vocabulary scoring based on word variety and complexity
      const varietyScore = Math.min(uniqueWords.size * 10, 100);
      const complexityScore = Math.min(avgWordLength * 15, 100);
      
      return (varietyScore + complexityScore) / 2;
    } catch (error) {
      return 75; // Default score
    }
  };

  const calculateGrammarScore = (text) => {
    try {
      const sentences = text.split(/[.!?]+/).filter(s => s.trim());
      const words = text.split(/\s+/).filter(w => w.trim());
      
      // Basic grammar heuristics
      const avgWordsPerSentence = words.length / Math.max(sentences.length, 1);
      const hasCapitalization = /[A-Z]/.test(text);
      const hasPunctuation = /[.!?]/.test(text);
      
      let score = 60; // Base score
      
      if (avgWordsPerSentence >= 5 && avgWordsPerSentence <= 20) score += 20;
      if (hasCapitalization) score += 10;
      if (hasPunctuation) score += 10;
      
      return Math.min(score, 100);
    } catch (error) {
      return 70; // Default score
    }
  };

  const generateFeedback = (overall, accuracy, fluency, prosody, pronunciation) => {
    if (overall >= 90) return "Excellent! Your speech is very clear and natural.";
    if (overall >= 80) return "Great job! Minor improvements could make it even better.";
    if (overall >= 70) return "Good work! Focus on speaking more naturally.";
    if (overall >= 60) return "Keep practicing! Work on clarity and fluency.";
    return "More practice needed. Focus on pronunciation basics.";
  };

  const clearResults = () => {
    setResult(null);
    setActiveTab('overview');
    setStatus({ message: 'Ready! Start recording to analyze your speech', type: 'success' });
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#28a745';
    if (score >= 70) return '#ffc107';
    if (score >= 60) return '#fd7e14';
    return '#dc3545';
  };

  const getScoreEmoji = (score) => {
    if (score >= 80) return '🎉';
    if (score >= 70) return '👍';
    if (score >= 60) return '📈';
    return '📚';
  };

  return (
    <div className="container">
      <style jsx>{`
        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          height: 100vh;
          color: #333;
  overflow-y: auto;
        }

        .header {
          text-align: center;
          margin-bottom: 30px;
          color: white;
        }

        .header h1 {
          font-size: 2.5rem;
          margin: 0 0 10px 0;
          font-weight: 700;
        }

        .header p {
          font-size: 1.2rem;
          margin: 0;
          opacity: 0.9;
        }

        .main-card {
          background: white;
          border-radius: 20px;
          padding: 30px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
          margin-bottom: 20px;
        }

        .config-section {
          background: #f8f9fa;
          padding: 25px;
          border-radius: 15px;
          margin-bottom: 20px;
        }

        .notice {
          background: #fff3cd;
          border: 1px solid #ffeaa7;
          padding: 15px;
          border-radius: 10px;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .instructions {
          background: #e3f2fd;
          border: 1px solid #90caf9;
          padding: 20px;
          border-radius: 10px;
          margin-bottom: 20px;
          text-align: center;
        }

        .instructions h3 {
          margin: 0 0 15px 0;
          color: #1565c0;
        }

        .controls {
          display: flex;
          gap: 15px;
          justify-content: center;
          flex-wrap: wrap;
          margin-bottom: 20px;
        }

        .btn {
          padding: 15px 30px;
          font-size: 1.1rem;
          font-weight: 600;
          border-radius: 10px;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 160px;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }

        .btn-primary {
          background: #007bff;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: #0056b3;
          transform: translateY(-2px);
        }

        .btn-danger {
          background: #dc3545;
          color: white;
        }

        .btn-danger:hover:not(:disabled) {
          background: #c82333;
          transform: translateY(-2px);
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #545b62;
          transform: translateY(-2px);
        }

        .status {
          padding: 15px;
          border-radius: 10px;
          font-weight: 600;
          text-align: center;
          font-size: 1.1rem;
          margin-top: 20px;
        }

        .status.success {
          background: #d4edda;
          border: 1px solid #c3e6cb;
          color: #155724;
        }

        .status.error {
          background: #f8d7da;
          border: 1px solid #f5c6cb;
          color: #721c24;
        }

        .status.warning {
          background: #fff3cd;
          border: 1px solid #ffeaa7;
          color: #856404;
        }

        .status.processing {
          background: #cce5ff;
          border: 1px solid #99d1ff;
          color: #004085;
        }

        .status.recording {
          background: #ffeaa7;
          border: 1px solid #ffdc5c;
          color: #856404;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.7; }
          100% { opacity: 1; }
        }

        .results-card {
          background: white;
          border-radius: 20px;
          padding: 30px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
          margin-top: 20px;
        }

        .tabs {
          display: flex;
          gap: 10px;
          margin-bottom: 30px;
          border-bottom: 2px solid #e9ecef;
          overflow-x: auto;
        }

        .tab {
          padding: 12px 24px;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 1rem;
          font-weight: 600;
          color: #6c757d;
          border-bottom: 3px solid transparent;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .tab.active {
          color: #007bff;
          border-bottom-color: #007bff;
        }

        .tab-content {
          min-height: 400px;
        }

        .overview-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .score-card {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 15px;
          text-align: center;
          border: 2px solid transparent;
          transition: all 0.3s ease;
        }

        .score-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1);
        }

        .score-card.main {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          grid-column: span 2;
        }

        .score-title {
          font-size: 1rem;
          margin-bottom: 10px;
          font-weight: 600;
        }

        .score-value {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 5px;
        }

        .score-emoji {
          font-size: 1.5rem;
        }

        .spoken-text {
          background: #e3f2fd;
          padding: 20px;
          border-radius: 15px;
          margin-bottom: 20px;
          border-left: 5px solid #2196f3;
        }

        .feedback {
          background: #f0f8ff;
          padding: 20px;
          border-radius: 15px;
          font-style: italic;
          font-size: 1.1rem;
          text-align: center;
          color: #1565c0;
        }

        .analysis-section {
          margin-bottom: 30px;
        }

        .analysis-title {
          font-size: 1.5rem;
          margin-bottom: 15px;
          color: #333;
          font-weight: 600;
        }

        .word-list {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 20px;
        }

        .word-item {
          background: #f8f9fa;
          padding: 8px 15px;
          border-radius: 20px;
          border: 2px solid #e9ecef;
          font-size: 0.9rem;
          transition: all 0.3s ease;
        }

        .word-item:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 10px rgba(0,0,0,0.1);
        }

        .phoneme-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
          gap: 10px;
        }

        .phoneme-item {
          background: #f8f9fa;
          padding: 10px;
          border-radius: 8px;
          text-align: center;
          font-size: 0.9rem;
          border: 2px solid #e9ecef;
        }

        .json-container {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 10px;
          overflow-x: auto;
        }

        .json-container pre {
          margin: 0;
          font-size: 0.85rem;
          line-height: 1.4;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 15px;
          margin-top: 20px;
        }

        .stat-item {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 10px;
          text-align: center;
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #007bff;
        }

        .stat-label {
          font-size: 0.9rem;
          color: #666;
          margin-top: 5px;
        }

        @media (max-width: 768px) {
          .container {
            padding: 10px;
          }
          
          .header h1 {
            font-size: 2rem;
          }
          
          .main-card {
            padding: 20px;
          }
          
          .controls {
            flex-direction: column;
            align-items: center;
          }
          
          .btn {
            width: 100%;
            max-width: 300px;
          }
          
          .overview-grid {
            grid-template-columns: 1fr;
          }
          
          .score-card.main {
            grid-column: span 1;
          }
        }
      `}</style>

      <div className="header">
        <h1>🎤 Advanced Speech Analyzer</h1>
        <p>Comprehensive pronunciation, fluency, and speech analysis</p>
      </div>

      <div className="main-card">
        <div className="config-section">
          <div className="notice">
            <span>⚠️</span>
            <div>
              <strong>Configuration Required:</strong> Update AZURE_SPEECH_KEY and AZURE_REGION in the code with your Azure Speech Service credentials.
            </div>
          </div>

          <div className="instructions">
            <h3>How to Use</h3>
            <p>
              1. Click "Start Recording" <br/>
              2. Speak clearly (word, sentence, or paragraph) <br/>
              3. Click "Stop Recording" <br/>
              4. Explore detailed analysis in the tabs below
            </p>
          </div>

          <div className="controls">
            <button
              className="btn btn-primary"
              onClick={startAssessment}
              disabled={isRecording || !sdkLoaded}
            >
              {isRecording ? (
                <>
                  <span>⏺️</span>
                  Recording...
                </>
              ) : (
                <>
                  <span>🎤</span>
                  Start Recording
                </>
              )}
            </button>
            
            <button
              className="btn btn-danger"
              onClick={stopAssessment}
              disabled={!isRecording}
            >
              <span>⏹️</span>
              Stop Recording
            </button>
            
            <button
              className="btn btn-secondary"
              onClick={clearResults}
              disabled={!sdkLoaded}
            >
              <span>🗑️</span>
              Clear Results
            </button>
          </div>

          <div className={`status ${status.type}`}>
            {status.message}
          </div>
        </div>
      </div>

      {result && (
        <div className="results-card">
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              📊 Overview
            </button>
            <button
              className={`tab ${activeTab === 'words' ? 'active' : ''}`}
              onClick={() => setActiveTab('words')}
            >
              📝 Word Analysis
            </button>
            <button
              className={`tab ${activeTab === 'phonemes' ? 'active' : ''}`}
              onClick={() => setActiveTab('phonemes')}
            >
              🔤 Phonemes
            </button>
            <button
              className={`tab ${activeTab === 'data' ? 'active' : ''}`}
              onClick={() => setActiveTab('data')}
            >
              💾 Raw Data
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'overview' && (
              <div>
                <div className="overview-grid">
                  <div className="score-card main">
                    <div className="score-title">Overall Score</div>
                    <div className="score-value">{result.overallScore}%</div>
                    <div className="score-emoji">{getScoreEmoji(result.overallScore)}</div>
                  </div>
                  
                  <div className="score-card">
                    <div className="score-title">Pronunciation</div>
                    <div className="score-value" style={{color: getScoreColor(result.pronunciation)}}>
                      {result.pronunciation}%
                    </div>
                  </div>
                  
                  <div className="score-card">
                    <div className="score-title">Fluency</div>
                    <div className="score-value" style={{color: getScoreColor(result.fluency)}}>
                      {result.fluency}%
                    </div>
                  </div>
                  
                  <div className="score-card">
                    <div className="score-title">Accuracy</div>
                    <div className="score-value" style={{color: getScoreColor(result.accuracy)}}>
                      {result.accuracy}%
                    </div>
                  </div>
                  
                  <div className="score-card">
                    <div className="score-title">Vocabulary</div>
                    <div className="score-value" style={{color: getScoreColor(result.vocabulary)}}>
                      {result.vocabulary}%
                    </div>
                  </div>
                  
                  <div className="score-card">
                    <div className="score-title">Grammar</div>
                    <div className="score-value" style={{color: getScoreColor(result.grammar)}}>
                      {result.grammar}%
                    </div>
                  </div>
                  
                  {result.hasProsody && (
                    <div className="score-card">
                      <div className="score-title">Prosody</div>
                      <div className="score-value" style={{color: getScoreColor(result.prosody)}}>
                        {result.prosody}%
                      </div>
                    </div>
                  )}
                </div>

                <div className="spoken-text">
                  <strong>You said:</strong> "{result.spokenText}"
                </div>

                <div className="feedback">
                  {result.feedback}
                </div>

                <div className="stats-grid">
                  <div className="stat-item">
                    <div className="stat-value">{result.phonemeAnalysis.length}</div>
                    <div className="stat-label">Phonemes</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-value">{result.isCorrect ? '✅' : '❌'}</div>
                    <div className="stat-label">Result</div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'words' && (
              <div>
                <div className="analysis-section">
                  <div className="analysis-title">Word-by-Word Analysis</div>
                  <div className="word-list">
                    {result.wordAnalysis.map((word, index) => (
                      <div
                        key={index}
                        className="word-item"
                        style={{
                          borderColor: getScoreColor(word.accuracyScore),
                          backgroundColor: word.errorType !== 'None' ? '#ffebee' : '#f8f9fa'
                        }}
                        title={`Accuracy: ${word.accuracyScore}% | Error: ${word.errorType} | Confidence: ${word.confidence}%`}
                      >
                        <div style={{ fontWeight: 'bold' }}>{word.word}</div>
                        <div style={{ fontSize: '0.8rem', color: '#666' }}>
                          {word.accuracyScore}% • {word.errorType}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="analysis-section">
                  <div className="analysis-title">Word Statistics</div>
                  <div className="stats-grid">
                    <div className="stat-item">
                      <div className="stat-value">
                        {result.wordAnalysis.filter(w => w.accuracyScore >= 80).length}
                      </div>
                      <div className="stat-label">Excellent Words</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-value">
                        {result.wordAnalysis.filter(w => w.accuracyScore >= 60 && w.accuracyScore < 80).length}
                      </div>
                      <div className="stat-label">Good Words</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-value">
                        {result.wordAnalysis.filter(w => w.accuracyScore < 60).length}
                      </div>
                      <div className="stat-label">Needs Work</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-value">
                        {Math.round(result.wordAnalysis.reduce((sum, w) => sum + w.accuracyScore, 0) / result.wordAnalysis.length)}%
                      </div>
                      <div className="stat-label">Avg Accuracy</div>
                    </div>
                  </div>
                </div>

                <div className="analysis-section">
                  <div className="analysis-title">Error Analysis</div>
                  <div className="word-list">
                    {result.wordAnalysis
                      .filter(word => word.errorType !== 'None')
                      .map((word, index) => (
                        <div
                          key={index}
                          className="word-item"
                          style={{
                            borderColor: '#dc3545',
                            backgroundColor: '#ffebee'
                          }}
                        >
                          <div style={{ fontWeight: 'bold', color: '#dc3545' }}>{word.word}</div>
                          <div style={{ fontSize: '0.8rem', color: '#666' }}>
                            Error: {word.errorType}
                          </div>
                        </div>
                      ))}
                  </div>
                  {result.wordAnalysis.filter(w => w.errorType !== 'None').length === 0 && (
                    <div style={{ textAlign: 'center', color: '#28a745', fontStyle: 'italic' }}>
                      🎉 No pronunciation errors detected!
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'phonemes' && (
              <div>
                <div className="analysis-section">
                  <div className="analysis-title">Phoneme Analysis</div>
                  <div className="phoneme-grid">
                    {result.phonemeAnalysis.map((phoneme, index) => (
                      <div
                        key={index}
                        className="phoneme-item"
                        style={{
                          borderColor: getScoreColor(phoneme.accuracyScore),
                          backgroundColor: phoneme.accuracyScore < 60 ? '#ffebee' : '#f8f9fa'
                        }}
                        title={`From word: ${phoneme.word} | Accuracy: ${phoneme.accuracyScore}%`}
                      >
                        <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                          /{phoneme.phoneme}/
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#666' }}>
                          {phoneme.accuracyScore}%
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#999' }}>
                          {phoneme.word}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="analysis-section">
                  <div className="analysis-title">Phoneme Statistics</div>
                  <div className="stats-grid">
                    <div className="stat-item">
                      <div className="stat-value">
                        {result.phonemeAnalysis.filter(p => p.accuracyScore >= 80).length}
                      </div>
                      <div className="stat-label">Excellent Phonemes</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-value">
                        {result.phonemeAnalysis.filter(p => p.accuracyScore >= 60 && p.accuracyScore < 80).length}
                      </div>
                      <div className="stat-label">Good Phonemes</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-value">
                        {result.phonemeAnalysis.filter(p => p.accuracyScore < 60).length}
                      </div>
                      <div className="stat-label">Needs Work</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-value">
                        {Math.round(result.phonemeAnalysis.reduce((sum, p) => sum + p.accuracyScore, 0) / result.phonemeAnalysis.length)}%
                      </div>
                      <div className="stat-label">Avg Accuracy</div>
                    </div>
                  </div>
                </div>

                <div className="analysis-section">
                  <div className="analysis-title">Challenging Phonemes</div>
                  <div className="phoneme-grid">
                    {result.phonemeAnalysis
                      .filter(phoneme => phoneme.accuracyScore < 70)
                      .sort((a, b) => a.accuracyScore - b.accuracyScore)
                      .slice(0, 10)
                      .map((phoneme, index) => (
                        <div
                          key={index}
                          className="phoneme-item"
                          style={{
                            borderColor: '#dc3545',
                            backgroundColor: '#ffebee'
                          }}
                        >
                          <div style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#dc3545' }}>
                            /{phoneme.phoneme}/
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#666' }}>
                            {phoneme.accuracyScore}%
                          </div>
                          <div style={{ fontSize: '0.7rem', color: '#999' }}>
                            {phoneme.word}
                          </div>
                        </div>
                      ))}
                  </div>
                  {result.phonemeAnalysis.filter(p => p.accuracyScore < 70).length === 0 && (
                    <div style={{ textAlign: 'center', color: '#28a745', fontStyle: 'italic' }}>
                      🎉 All phonemes pronounced well!
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'data' && (
              <div>
                <div className="analysis-section">
                  <div className="analysis-title">Raw JSON Response</div>
                  <div className="json-container">
                    <pre>{JSON.stringify(result.rawJson, null, 2)}</pre>
                  </div>
                </div>

                <div className="analysis-section">
                  <div className="analysis-title">Technical Details</div>
                  <div className="stats-grid">
                    <div className="stat-item">
                      <div className="stat-value">{result.wordCount}</div>
                      <div className="stat-label">Word Count</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-value">{result.phonemeAnalysis.length}</div>
                      <div className="stat-label">Phoneme Count</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-value">{result.hasProsody ? 'Yes' : 'No'}</div>
                      <div className="stat-label">Prosody Available</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-value">{result.spokenText.length}</div>
                      <div className="stat-label">Character Count</div>
                    </div>
                  </div>
                </div>

                <div className="analysis-section">
                  <div className="analysis-title">Score Breakdown</div>
                  <div className="json-container">
                    <pre>{JSON.stringify({
                      overallScore: result.overallScore,
                      pronunciation: result.pronunciation,
                      accuracy: result.accuracy,
                      fluency: result.fluency,
                      prosody: result.prosody,
                      vocabulary: result.vocabulary,
                      grammar: result.grammar,
                      isCorrect: result.isCorrect,
                      feedback: result.feedback
                    }, null, 2)}</pre>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PronunciationChecker;
